import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Send, Star, Gift, ArrowRight } from 'lucide-react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db, collections } from '@/lib/firebase';
import { getEvaluatorById } from '@/services/evaluator';
import { getAutomationById } from '@/services/automation';
import { generateEvaluatorDocId } from '@/services/evaluator/utils';
import { toast } from 'react-hot-toast';
import { Link } from 'react-router-dom';
import Logo from '@/components/Logo';

export default function ThankYou() {
  const navigate = useNavigate();
  const { evaluatorId } = useParams();
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [evaluatorData, setEvaluatorData] = useState<{
    firstName: string;
    lastName: string;
    email: string;
    originalEmail: string;
    promoCode: string;
    subjectName: string;
    userId: string;
    promoCodeSent?: boolean;
  } | null>(null);

  useEffect(() => {
    async function loadData() {
      if (!evaluatorId) {
        toast.error('Invalid access: missing evaluator ID');
        navigate('/');
        return;
      }

      try {
        const evaluator = await getEvaluatorById(evaluatorId);
        if (!evaluator) {
          throw new Error('Evaluator not found');
        }

        // Set initial sent state based on evaluator data
        setSent(!!evaluator.promoCodeSent);

        setEvaluatorData({
          firstName: evaluator.firstName,
          lastName: evaluator.lastName,
          email: evaluator.email,
          originalEmail: evaluator.originalEmail,
          promoCode: evaluator.promoCode,
          subjectName: evaluator.subjectName,
          userId: evaluator.userId,
          promoCodeSent: evaluator.promoCodeSent
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unable to load evaluator information';
        console.error('Error loading evaluator:', message);
        toast.error(message);
        navigate('/');
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [evaluatorId, navigate]);

  const handleSendCode = async () => {
    if (!evaluatorData || !evaluatorId || sending || sent) {
      toast.error('Unable to send promo code at this time');
      return;
    }

    const email = evaluatorData.originalEmail || evaluatorData.email;
    if (!email) {
      toast.error('Unable to send promo code: invalid email');
      return;
    }

    setSending(true);

    try {
      // First update the evaluator document
      const docId = generateEvaluatorDocId(evaluatorId, evaluatorData.userId);
      const evaluatorRef = doc(db, collections.evaluators, docId);
      
      await updateDoc(evaluatorRef, {
        promoCodeSent: true,
        promoCodeSentAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Get the webhook URL from Firestore
      const automation = await getAutomationById('evaluator-promo-code');
      if (!automation?.url) {
        throw new Error('Service temporarily unavailable. Please try again later.');
      }

      // Prepare payload
      const payload = {
        firstName: evaluatorData.firstName,
        lastName: evaluatorData.lastName,
        email: evaluatorData.email.toLowerCase().trim(),
        promoCode: evaluatorData.promoCode,
        evaluatorId,
        userId: evaluatorData.userId,
        action: 'evaluator_promo_code',
        timestamp: new Date().toISOString(),
        environment: import.meta.env.MODE || 'development'
      };

      // Send request to webhook
      const response = await fetch(automation.url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Automation-Type': 'evaluator-promo-code'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        let errorMessage = 'Service temporarily unavailable. Please try again later.';
        try {
          const errorData = await response.json();
          if (errorData?.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // Use default error message if JSON parsing fails
        }
        throw new Error(errorMessage);
      }

      setSent(true);
      toast.success('Discount code sent successfully! Please check your email.');
      
      // Update local state
      setEvaluatorData(prev => prev ? {
        ...prev,
        promoCodeSent: true,
        promoCodeSentAt: serverTimestamp()
      } : null);
    } catch (error) {
      let errorMessage = 'Failed to send promo code. Please try again.';
      
      if (error instanceof Error) {
        if (error.message.includes('permission-denied')) {
          errorMessage = 'Service temporarily unavailable. Please try again later.';
        } else {
          errorMessage = error.message;
        }
      }
      console.error('Error sending promo code:', error);
      toast.error(errorMessage);
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-dark-lighter to-dark flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  if (!evaluatorData) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-dark-lighter to-dark flex flex-col">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex-1 flex items-center justify-center px-4 pb-12"
      >
        <div className="max-w-4xl w-full mx-4">
          <div className="relative">
            {/* Background decorative elements */}
            <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-secondary/10 to-primary/10 rounded-3xl blur-2xl opacity-30" />
            <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-secondary/5 rounded-3xl blur-xl opacity-40" />

            {/* Main content */}
            <div className="relative bg-white rounded-2xl shadow-xl border border-white/20 p-8 md:p-12">
              <div className="flex justify-center mb-8">
                <Link to="/">
                  <Logo className="h-12" />
                </Link>
              </div>

              <div className="text-center mb-12">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", duration: 0.8 }}
                  className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-r from-primary to-secondary p-0.5"
                >
                  <div className="w-full h-full rounded-full bg-white flex items-center justify-center">
                    <Check className="w-10 h-10 text-primary" />
                  </div>
                </motion.div>

                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="text-4xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent mb-4"
                >
                  One Last Thing!
                </motion.h1>

                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="text-xl text-gray-600"
                >
                  We truly appreciate the time you took to complete this evaluation. Your evaluation has been received, and your input will help {evaluatorData.subjectName} keep growing.
                </motion.p>
              </div>

              <div className="grid grid-cols-1 gap-8 mb-12 max-w-lg mx-auto">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="bg-gradient-to-br from-primary/5 via-secondary/5 to-primary/5 rounded-xl p-6"
                >
                  <div className="bg-white/50 backdrop-blur-sm rounded-lg p-6">
                    <div className="flex justify-center">
                      <Gift className="w-12 h-12 text-secondary mb-4" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 text-center">We Have A Special Offer For You</h3>
                    <div className="space-y-2">
                      <p className="text-2xl font-bold text-primary text-center w-full">20% Off On Any Product</p>
                      <p className="text-gray-600 text-center">Get your discount code and start your own growth journey</p>
                    </div>
                  </div>
                </motion.div>
              </div>

              <div className="max-w-md mx-auto">
                <AnimatePresence mode="wait">
                  {sent ? (
                    <motion.div
                      key="success"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      className="bg-green-50 border border-green-200 rounded-xl p-6 text-center"
                    >
                      <Check className="w-12 h-12 text-green-500 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-green-800 mb-2">
                        Discount Code Sent!
                      </h3>
                      <p className="text-green-600 mb-4">
                        Your exclusive discount code has been sent to your email.
                      </p>
                      <div className="bg-green-100 p-4 rounded-lg">
                        <p className="text-green-800 font-medium">Your Code: {evaluatorData.promoCode}</p>
                      </div>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="cta"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      className="text-center"
                    >
                      <button
                        onClick={handleSendCode}
                        disabled={sending}
                        className="w-full bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90 text-dark font-medium py-4 px-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center space-x-3"
                      >
                        {sending ? (
                          <>
                            <div className="animate-spin rounded-full h-5 w-5 border-2 border-dark border-t-transparent"></div>
                            <span>Sending...</span>
                          </>
                        ) : (
                          <>
                            <Send className="w-5 h-5" />
                            <span>Get My Exclusive Discount</span>
                          </>
                        )}
                      </button>
                      <p className="text-sm text-gray-500 mt-4">
                        Your discount code will be sent to {evaluatorData.email}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}